import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import { createQueryKey, invalidateGlobalsQueryKey, FINANCIAL_MODEL_SETTINGS_BASE_KEY } from '@/api/query-keys'
import {
  EquitySetup,
  EquityPeriodResponse,
  TotalsEquityModalResponse,
  UpdateEquitySetup,
  UpdateEquityPeriod,
  EquityAllowedTypes,
  EquityBaseFieldsKeys
} from '../types/equity'
import { PeriodTypeQuery, PeriodTypeView } from '@/types'
import { CAPITAL_STRUCTURE_TOTALS_KEY } from './totals'

// Query keys
const EQUITY_BASE_KEY = [`capital-structure`, `equity`]

export const EQUITY_SETUP_KEY = createQueryKey({
  baseKey: EQUITY_BASE_KEY,
  keys: [`setup`]
})

export const EQUITY_DATA_ENTRY_KEY = createQueryKey({
  baseKey: EQUITY_BASE_KEY,
  keys: [`data-entry`]
})

export const EQUITY_TOTALS_KEY = CAPITAL_STRUCTURE_TOTALS_KEY

// Setup endpoints
const getEquitySetup = async (financial_model_id: string) => {
  const { data } = await axiosInstance.get<EquitySetup>(`/financial-models/${financial_model_id}/capital-structure/equity/setup`)
  return data
}

export const useGetEquitySetup = (financial_model_id: string) =>
  useSuspenseQuery({
    queryKey: EQUITY_SETUP_KEY.concat([financial_model_id]),
    queryFn: () => getEquitySetup(financial_model_id)
  })

type UpdateEquitySetupParams = {
  financial_model_id: string
  data: Partial<UpdateEquitySetup>
}

const updateEquitySetup = async ({ financial_model_id, data }: UpdateEquitySetupParams) => {
  const { data: response } = await axiosInstance.post<EquitySetup>(`/financial-models/${financial_model_id}/capital-structure/equity/setup`, data)
  return response
}

export const useUpdateEquitySetup = ({ financial_model_id }: Pick<UpdateEquitySetupParams, 'financial_model_id'>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<UpdateEquitySetup>) => updateEquitySetup({ financial_model_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EQUITY_SETUP_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: EQUITY_DATA_ENTRY_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: EQUITY_TOTALS_KEY.concat([financial_model_id])
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

// Data entry endpoints
type GetEquityDataEntryParams = {
  financial_model_id: string
  type: PeriodTypeQuery
  view: PeriodTypeView
}

const getEquityDataEntry = async ({ financial_model_id, type, view }: GetEquityDataEntryParams) => {
  const { data } = await axiosInstance.get<EquityPeriodResponse>(`/financial-models/${financial_model_id}/capital-structure/equity/data-entry`, {
    params: { type, view }
  })
  return data
}

export const useGetEquityDataEntry = ({ financial_model_id, type, view }: GetEquityDataEntryParams) =>
  useSuspenseQuery({
    queryKey: EQUITY_DATA_ENTRY_KEY.concat([financial_model_id, type, view]),
    queryFn: () => getEquityDataEntry({ financial_model_id, type, view })
  })

const getEquityHistoricalDataEntry = async ({ financial_model_id, view }: Omit<GetEquityDataEntryParams, 'type'>) =>
  getEquityDataEntry({ financial_model_id, type: `historical`, view })

export const useGetEquityHistoricalDataQuery = ({ financial_model_id, view }: Omit<GetEquityDataEntryParams, 'type'>) =>
  useQuery<EquityPeriodResponse>({
    queryKey: EQUITY_DATA_ENTRY_KEY.concat([financial_model_id, `historical`, view]),
    queryFn: () => getEquityHistoricalDataEntry({ financial_model_id, view }),
    enabled: Boolean(financial_model_id)
  })

// Update Equity period data
const updateEquityPeriodData = async (financialModelId: string, payload: UpdateEquityPeriod) => {
  const { data } = await axiosInstance.post(`/financial-models/${financialModelId}/capital-structure/equity/data-entry`, payload)
  return data
}

export const useUpdateEquityPeriodData = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateEquityPeriod) => updateEquityPeriodData(financialModelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EQUITY_DATA_ENTRY_KEY.concat([financialModelId])
      })
      queryClient.resetQueries({
        queryKey: EQUITY_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

export const useBulkUpdateEquityPeriodData = (financialModelId: string) =>
  useMutation({
    mutationFn: (payload: UpdateEquityPeriod) => updateEquityPeriodData(financialModelId, payload)
  })

// Totals endpoints
type GetEquityTotalsParams = {
  financial_model_id: string
  type: PeriodTypeQuery
  view: PeriodTypeView
}

const getEquityTotals = async ({ financial_model_id, type, view }: GetEquityTotalsParams) => {
  const { data } = await axiosInstance.get<TotalsEquityModalResponse>(
    `/financial-models/${financial_model_id}/capital-structure/equity/data-entry/totals`,
    { params: { type, view } }
  )
  return data
}

export const useGetEquityTotals = ({ financial_model_id, type, view }: GetEquityTotalsParams) =>
  useSuspenseQuery({
    queryKey: EQUITY_TOTALS_KEY.concat([financial_model_id, type, view]),
    queryFn: () => getEquityTotals({ financial_model_id, type, view })
  })

// Column config endpoints
type SwitchEquityColumnTypeParams = {
  financial_model_id: string
  input_type: EquityAllowedTypes
  column_name: EquityBaseFieldsKeys
  month?: number
  year?: number
  product_id?: string | null
  category_id?: string | null
}

const switchEquityColumnType = async (params: SwitchEquityColumnTypeParams) => {
  const { financial_model_id, column_name, ...rest } = params
  const { data } = await axiosInstance.post(`/financial-models/${financial_model_id}/capital-structure/equity/config/${column_name}`, undefined, {
    params: rest
  })
  return data
}

export const useSwitchEquityColumnType = (financial_model_id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: SwitchEquityColumnTypeParams) => switchEquityColumnType(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EQUITY_SETUP_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: EQUITY_DATA_ENTRY_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: EQUITY_TOTALS_KEY.concat([financial_model_id])
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}
